<template>
  <div class="wrapper">
    <div class="container">
      <div class="content" v-if="status === 'loading'">
        <h1>{{ $gettext('Verifying your email') }}</h1>

        <div>
          <p>{{ $gettext('Our hamsters are hard at work verifying your email, please wait a moment.') }}</p>
        </div>
      </div>

      <div class="content" v-else-if="status === 'success'">
        <h1>{{ $gettext('Email verified!') }}</h1>

        <div>
          <p>{{ $gettext('Your email has now been verified!') }}</p>
          <p>{{ $gettext('You may now send invoices, quotations and reminders with Gekko.') }}</p>
        </div>

        <GekkoButton :text="$gettext('Take me to my dashboard!')" @click="() => $router.push({ name: 'Dashboard' })" />
      </div>

      <div class="content" v-else>
        <h1>{{ $gettext('Critical failure') }}</h1>

        <div v-if="status === 'notfound'">
          <p>{{ $gettext('Oh no, it appears that this verification link has expired or is invalid.') }}</p>
          <p>{{ $gettext("Click the button below and we'll send you another email.") }}</p>
        </div>
        <div v-else-if="status === 'forbidden'">
          <p>{{ $gettext('This link appears to be for someone else.') }}</p>
          <p>
            {{
              $gettext(
                'Log out and log into the account with the email this verification link was received on to verify your email.'
              )
            }}
          </p>
        </div>
        <div v-else>
          <p>{{ $gettext('An unexpected error occurred on our servers.') }}</p>
          <p>{{ $gettext('Please try again later.') }}</p>
        </div>

        <GekkoButton
          v-if="status === 'notfound'"
          :text="$gettext('Send me another verification email')"
          @click="sendVerificationEmail"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import ProfileService from '@/store/services/profile.service'
import GekkoButton from '@/components/GekkoButton.vue'

export default {
  name: 'VerifyEmail',
  components: { GekkoButton },
  data() {
    return {
      status: 'loading',
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'main/getCurrentUser',
    }),
  },
  mounted() {
    this.setupData()
  },
  methods: {
    ...mapMutations({
      setEmailValidated: 'main/SET_CURRENT_USER_EMAIL_VALIDATED'
    }),
    setupData () {
      if (this.currentUser && this.currentUser.profile && this.currentUser.profile.active_email) {
        return this.$router.push({ name: 'Dashboard' })
      }

      ProfileService.verifyEmail(this.$route.params.token).then(() => {
        this.status = 'success'
        this.setEmailValidated(true)
      }).catch((error) => {
        if (error.status === 401 || error.status === 400) {
          this.status = 'unauthorized'
          this.$router.push({
            name: 'Login',
            query: {
              redirect: this.$route.fullPath,
            },
          })
        } else if (error.status === 403) {
          this.status = 'forbidden'
        } else if (error.status === 404) {
          this.status = 'notfound'
        } else {
          this.status = 'error'
        }
      })
    },
    sendVerificationEmail() {
      ProfileService.sendVerificationEmail()
        .then(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Email sent'),
            text: this.$gettext('A verification email has been sent, check your email address to verify your email.'),
            variant: 'success',
            duration: 4,
          })
        })
        .catch(() => {
          this.$store.commit('setAlert', {
            title: this.$gettext('Failed to send email'),
            text: this.$gettext('We failed to send you an email, please try again later.'),
            variant: 'danger',
            duration: 4,
          })
        })
    },
  },
}
</script>

<style scoped lang="scss">
.wrapper {
  display: flex;
  align-content: center;
  justify-content: center;

  .container {
    display: flex;
    flex-direction: column;
    background-color: white;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    border-radius: 10px;
    padding: 2rem;
    max-width: 500px;

    h1 {
      margin: 0;
    }

    p {
      margin: 0;
    }

    .content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }
}

a {
  color: #22ac8a;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #26cda4;
  }
}
</style>